import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Edit, Clock, AlertCircle, User, Calendar, FileText, Target } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import recommendationService from '../../../../../../services/audit_services/recommendationService';

export default function RecommendationDetails() {
  const { auditPlanId, sectionId, questionId, recommendationId } = useParams();
  const navigate = useNavigate();
  const { authTokens, user } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  const [recommendation, setRecommendation] = useState(null);
  const [loading, setLoading] = useState(true);

  // Context data from API
  const [auditPlan, setAuditPlan] = useState(null);
  const [userRole, setUserRole] = useState(null);

  const isOwner = user && recommendation && user.id === recommendation.recommendation_owner_id;
  const canEdit = userRole === 'auditor' && auditPlan?.status === 'in_progress';

  useEffect(() => {
    fetchRecommendationDetails();
  }, [auditPlanId, sectionId, questionId, recommendationId]);

  const fetchRecommendationDetails = async () => {
    setLoading(true);
    
    try {
      const result = await recommendationService.getRecommendationDetail(
        authTokens.access, 
        auditPlanId, 
        sectionId, 
        questionId, 
        recommendationId
      );
      
      if (result.success) {
        setRecommendation(result.data);
        setAuditPlan(result.data.audit_plan);
        setUserRole(result.data.my_role_in_audit_plan);
      } else {
        showError(result.error || 'Error loading recommendation details');
        navigate(`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/recommendations`);
      }
    } catch (error) {
      console.error('Error fetching recommendation details:', error);
      showError('Error loading recommendation details');
      navigate(`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/recommendations`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'En attente', icon: Clock },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'En cours', icon: Clock },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Terminé', icon: Clock },
      overdue: { bg: 'bg-red-100', text: 'text-red-800', label: 'En retard', icon: AlertCircle }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        <Icon className="h-4 w-4 mr-1" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des détails de la recommandation...</p>
        </div>
      </div>
    );
  }

  if (!recommendation) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Recommandation non trouvée</p>
        <Link 
          to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/recommendations`} 
          className="text-green-600 hover:underline mt-4 inline-block"
        >
          Retour aux recommandations
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/recommendations`}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Détails de l'action de recommandation</h1>
            <p className="text-sm text-gray-600">
              Créé le {new Date(recommendation.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {getStatusBadge(recommendation.status)}
          {canEdit && (
            <Link
              to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/recommendations/${recommendationId}/edit`}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Edit className="h-4 w-4 mr-2" />
              Modifier
            </Link>
          )}
        </div>
      </div>

      {/* Access Control Banner */}
      {!canEdit && auditPlan && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
            <div>
              <p className="text-sm font-medium text-yellow-800">
                {userRole !== 'auditor' 
                  ? 'Seuls les auditeurs peuvent modifier les actions de recommandation'
                  : 'Les actions de recommandation ne peuvent être modifiées que lorsque le plan d\'audit est en cours'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Question Context */}
      {recommendation.question && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <FileText className="h-4 w-4 text-green-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-green-900">Question associée</h3>
              <Link
                to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}`}
                className="text-sm text-green-700 hover:text-green-900 font-medium"
              >
                {recommendation.question.question_name}
              </Link>
              <div className="flex items-center space-x-4 mt-2 text-xs text-green-600">
                <span>Section: {recommendation.question.audit_section?.section_name}</span>
                <span>Statut: {recommendation.question.status}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recommendation Details */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Informations sur la recommandation</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            {/* Owner */}
            <div>
              <div className="flex items-center mb-2">
                <User className="h-4 w-4 text-gray-400 mr-2" />
                <p className="text-sm font-medium text-gray-700">Propriétaire de la recommandation</p>
              </div>
              <div className="ml-6">
                <p className="text-sm font-medium text-gray-900">{recommendation.owner_name}</p>
                <p className="text-sm text-gray-500">{recommendation.owner_email}</p>
              </div>
            </div>

            {/* Target Completion Date */}
            <div>
              <div className="flex items-center mb-2">
                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                <p className="text-sm font-medium text-gray-700">Date d'achèvement cible</p>
              </div>
              <p className="text-sm text-gray-900 ml-6">
                {recommendation.delay_of_recommendation 
                  ? new Date(recommendation.delay_of_recommendation).toLocaleDateString()
                  : 'Non défini'
                }
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Created By */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Créé par</p>
              <p className="text-sm text-gray-900">{recommendation.created_by_name || 'Inconnu'}</p>
            </div>

            {/* Dates */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Dates importantes</p>
              <div className="space-y-1 text-sm text-gray-900">
                <p>Créé: {new Date(recommendation.created_at).toLocaleDateString()}</p>
                <p>Mis à jour: {new Date(recommendation.updated_at).toLocaleDateString()}</p>
                {recommendation.started_at && (
                  <p>Commencé: {new Date(recommendation.started_at).toLocaleDateString()}</p>
                )}
                {recommendation.completed_at && (
                  <p>Terminé: {new Date(recommendation.completed_at).toLocaleDateString()}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Question Recommendation */}
      {(recommendation.recommendation_text || recommendation.question_recommendation) && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recommandation originale de la question</h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-900">
              {recommendation.recommendation_text || 
               (typeof recommendation.question_recommendation === 'object' 
                 ? recommendation.question_recommendation?.recommendation 
                 : recommendation.question_recommendation)}
            </p>
            {recommendation.question_recommendation?.priority && (
              <div className="mt-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  recommendation.question_recommendation.priority === 'high' ? 'bg-red-100 text-red-800' :
                  recommendation.question_recommendation.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  Priorité: {recommendation.question_recommendation.priority}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Elements of Recommendation */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <Target className="h-5 w-5 text-green-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Éléments de la recommandation</h3>
        </div>
        <div className="prose max-w-none">
          <p className="text-sm text-gray-900 whitespace-pre-wrap">{recommendation.elements_of_recommendation}</p>
        </div>
      </div>

      {/* KPI of Recommendation */}
      {recommendation.kpi_of_recommendation && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Indicateurs de performance clés</h3>
          <div className="prose max-w-none">
            <p className="text-sm text-gray-900 whitespace-pre-wrap">{recommendation.kpi_of_recommendation}</p>
          </div>
        </div>
      )}

 
    </div>
  );
}